
import { Button, Checkbox, Alert } from 'element-ui';
import StripePayment from '@/services/payments/StripePayment';
import cartMixin from '@/mixins/cartMixin';
import addressMixin from '@/mixins/addressMixin';
import checkoutPaymentMixin from '@/mixins/checkoutPaymentMixin';

import { loadStripe } from '@stripe/stripe-js';

export default {
  mixins: [cartMixin, addressMixin, checkoutPaymentMixin],
  props: {
    paymentMethod: {
      type: Object,
    },
    paymentMethodTypes: {
      type: String,
      default: 'card', //'card,klarna,...'
    },
    name: {
      type: String,
      default: 'card', //'card,klarna,...'
    },
  },
  data() {
    return {
      token: null,
      elements: null,
      clientSecret: null,
      stripe: null,
      generalConditionsSale: false,
      loading: true,
      paymentElementMounted: false,
    };
  },
  computed: {
    stripePayment() {
      return new StripePayment({
        cart: this.cart,
        auth: this.$auth,
        http: this.$http,
        addressService: this.addressService,
      });
    },
  },
  methods: {
    async initialize() {
      try {
        // Always reuse the shared client secret from the store if Express Checkout created it
        const existingSecret = this.$store.state.checkout?.stripeClientSecret;
        const isExpressActive = this.$store.state.checkout?.isExpressActive;

        if (isExpressActive && existingSecret) {
          // Express Checkout already owns the PaymentIntent — just reuse it
          this.clientSecret = existingSecret;
        } else {
          // No express session — create or update our own PaymentIntent
          const data = await this.stripePayment.intent({
            paymentMethodTypes: this.paymentMethodTypes,
            isDigital: this.isDigitalCart,
            clientSecret: existingSecret,
          });

          if (data?.ClientSecret) {
            this.$store.commit('checkout/setStripeClientSecret', data.ClientSecret);
            this.clientSecret = data.ClientSecret;
          }
        }

        this.loading = false;

        if (!this.clientSecret) {
          return;
        }

        // Wait for Vue to render the v-if="stripe" block so the DOM target exists
        await this.$nextTick();

        // Safety check: verify the mount target actually exists in the DOM
        const mountTarget = document.querySelector(`#${this.name}-payment-element`);
        if (!mountTarget) {
          console.warn(`[Stripe Form] Mount target #${this.name}-payment-element not found, skipping.`);
          return;
        }

        // Don't re-mount if already mounted with the same secret
        if (this.paymentElementMounted && this.elements) {
          return;
        }

        this.elements = this.stripe.elements({
          clientSecret: this.clientSecret,
          locale: this.$i18n.locale ?? 'fr',
        });

        const paymentElementOptions = {
          layout: 'tabs',
        };

        if (this.isDigitalCart) {
          paymentElementOptions.fields = {
            billingDetails: {
              address: 'auto',
            },
          };
        }

        const paymentElement = this.elements.create(
          'payment',
          paymentElementOptions
        );

        paymentElement.mount(`#${this.name}-payment-element`);
        this.paymentElementMounted = true;
      } catch (error) {
        console.error('stripe : ', error);
        this.error = error;
      }

      this.loading = false;
    },
    async handleSubmit() {
      if (this.submitLoading) return;
      if (!this.checkFormOk()) return;
      const saved = await this.ensureAddressesSaved();
      if (!saved) return;

      if (this.clientSecret) {
        // Before confirming, ensure intent is up-to-date with latest cart total
        // (only if Express is not active — otherwise Express manages the intent)
        const isExpressActive = this.$store.state.checkout?.isExpressActive;
        if (!isExpressActive) {
          const freshData = await this.stripePayment.intent({
            paymentMethodTypes: this.paymentMethodTypes,
            isDigital: this.isDigitalCart,
            clientSecret: this.clientSecret,
          });
          if (freshData?.ClientSecret) {
            this.$store.commit('checkout/setStripeClientSecret', freshData.ClientSecret);
            this.clientSecret = freshData.ClientSecret;
          }
        }

        if (await this.postData()) {
          this.toggleLoader(true);
          const idorder = this.cart.IdCart;

          const { error, paymentIntent } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
              return_url: `${process.env.URL}cart/thanks/accepted?orderid=${idorder}&cartid=${this.cartService.idCart}`,
            },
            redirect: 'if_required',
          });

          if (error) {
            this.showMessage(error.message);
          } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            this.addPaymentInfoGtm(this.name);

            this.$router.push({
              path: this.localePath(
                `/cart/thanks/accepted?orderid=${idorder}&cartid=${this.cartService.idCart}`
              ),
            });
          }

          this.toggleLoader(false);
        }
      }
    },
    async postData() {
      try {
        const response = await this.stripePayment.postData({
          clientSecret: this.clientSecret,
          paymentMethod: this.paymentMethod,
        });
        return true;
      } catch (error) {
        return false;
      }
    },
    showMessage(messageText) {
      if (process.client) {
        const messageContainer = document.querySelector(
          `#${this.name}-payment-message`
        );
        if (!messageContainer) return;

        messageContainer.classList.remove('hidden');
        messageContainer.textContent = messageText;

        setTimeout(function () {
          messageContainer.classList.add('hidden');
          messageContainer.textContent = '';
        }, 4000);
      }
    },
    toggleLoader(isLoading) {
      this.$store.commit('shared/wind/setIsLoading', isLoading);
    },
  },
  async mounted() {
    const stripe = await loadStripe(process.env.STRIPE_PUBLIC_KEY);
    this.stripe = stripe;
    // Wait for Vue to render the v-if="stripe" conditional DOM before mounting
    await this.$nextTick();
    await this.initialize();
  },
  beforeDestroy() {
    this.paymentElementMounted = false;
  },
  watch: {
    'cart.Total.ToPay.TaxIncl'(newTotal, oldTotal) {
      // ONLY update if we're not already syncing via Express Checkout
      const isExpressSyncing = this.$store.state.checkout?.isExpressSyncing;
      const isExpressActive = this.$store.state.checkout?.isExpressActive;

      if (!isExpressSyncing && !isExpressActive && newTotal !== oldTotal && this.clientSecret && !this.loading) {
        // Reset mounted flag so we can re-create the element with the new intent
        this.paymentElementMounted = false;
        this.initialize();
      }
    }
  },
  components: {
    Button,
    Checkbox,
    Alert,
  },
};
